import { BUILT_GROUPS } from "@/lib/built-catalogue";
import { resolveCaseStudyRef } from "@/lib/case-studies";

/**
 * WHAT OWNLY CAN BUILD, ARRANGED SO SOMEBODY CAN FIND THEIR OWN PROBLEM IN IT.
 *
 * ────────────────────────────────────────────────────────────────────────────────
 *  THE DISTINCTION THIS FILE EXISTS TO KEEP: a service is what we CAN build. A
 *  case study and a `/built` entry are what we HAVE built. They are different
 *  claims and they are stored in different fields, so a page cannot quietly turn
 *  one into the other.
 * ────────────────────────────────────────────────────────────────────────────────
 *
 * `canBuild` is capability. It may describe work we have not sold as a standalone
 * project, because the technical capability is real and a buyer is entitled to know
 * we can do it. `builtIds` and `caseStudySlugs` are evidence, and every value in
 * them is validated below against the real catalogue and the real case studies. A
 * typo fails the build rather than rendering a proof section with a hole in it.
 *
 * WHY A REGISTRY AND NOT ELEVEN HAND-WRITTEN PAGES. The same reasoning as
 * `sectors.ts`. These pages point at `/built` entries, at case studies, at each
 * other and at industries, and every one of those relationships would otherwise be
 * written down twice and rot on the second copy. Declared once, adding a service
 * wires its hub card, its route, its sitemap entry, its breadcrumb and its links in
 * both directions, and `check-definition-of-done` sees an unknown id immediately.
 *
 * TWO SERVICES DELIBERATELY HAVE NO PAGE HERE, and that is not an oversight.
 * `/custom-software-for-businesses` and `/business-automation-systems` already
 * exist, are substantial, and have whatever search equity they have earned. Giving
 * them a second home under `/services/` would be two pages competing for one
 * query, which is the cannibalisation this architecture is supposed to prevent. So
 * they appear in the taxonomy with their real `href` and no `page`, and the route
 * generator skips them.
 *
 * THE BAR FOR A PAGE OF ITS OWN: somebody searching for exactly this thing, landing
 * here and reading nothing else, gets a substantial and genuinely different answer.
 * Where two candidates would have said 80% of the same thing, they were merged and
 * the merge is recorded in the comment on the survivor. That list is in the report,
 * not invented later: dashboards and reporting sit inside internal systems, MVP and
 * product development sit inside SaaS, third-party integrations and API work sit in
 * one page, and legacy replacement is a section of the custom software page rather
 * than a page that would have to be written entirely in the conditional.
 */

export type ServiceCategorySlug =
  | "software-development"
  | "business-systems"
  | "automation-and-integration"
  | "web-and-commerce";

export type ServiceCategory = {
  slug: ServiceCategorySlug;
  name: string;
  /** One line on the hub, above the services in this category. */
  blurb: string;
};

export type ServicePage = {
  /** `<h1>`. Written as the thing, not as a keyword. */
  heading: string;
  /** `<title>`. Names the service in the words somebody searches for. */
  metaTitle: string;
  metaDescription: string;
  /** The paragraph under the heading. */
  intro: string;
  /** What the thing actually is, for somebody who is not sure they are in the right place. */
  whatItIs: readonly string[];
  /** Signs you need it. Symptoms of a business, never a claim about this reader's business. */
  signs: readonly string[];
  /** CAPABILITY. Things we can build. Not a claim that any of them has been sold before. */
  canBuild: readonly string[];
  /** How the work runs, in the terms this service actually changes. */
  approach: readonly string[];
  /** EVIDENCE. Ids from the built catalogue. Validated below. */
  builtIds: readonly string[];
  /** EVIDENCE. Case study slugs, or `slug#website` for a build's website on its own. Validated below. Empty is allowed and is honest. */
  caseStudySlugs: readonly string[];
  /** Services worth reading next. Validated below. */
  relatedSlugs: readonly string[];
  /**
   * Questions this service page answers, rendered on the page and marked up as FAQPage.
   *
   * Optional, and it should stay that way. A service page earns questions when search sends it
   * people who arrived with one, which is not every service. Where it does, answering them is
   * what turns a result at the bottom of page one into a click: an unadorned blue link there
   * gets ignored, and the questions are also the part an answer engine can quote.
   *
   * Google's rule: every answer must be visible on the page and complete on its own. The
   * template renders these, so using the field satisfies that.
   */
  faqs?: readonly { question: string; answer: string }[];
};

export type Service = {
  /** Identity, and the last path segment for services that get a generated page. */
  slug: string;
  /** Where the service actually lives. Not derived, because two of them predate this file. */
  href: `/${string}`;
  /** Hub card and nav label. The noun somebody would use for it. */
  name: string;
  category: ServiceCategorySlug;
  /** One line under the name on the hub. */
  hubBlurb: string;
  /** Absent for the two services that already have their own page elsewhere. */
  page?: ServicePage;
};

export const SERVICE_CATEGORIES: readonly ServiceCategory[] = [
  {
    slug: "software-development",
    name: "Software development",
    blurb:
      "Building the thing itself, when what a business needs does not exist as a product it can buy.",
  },
  {
    slug: "business-systems",
    name: "Business systems",
    blurb:
      "The software a business runs on internally: where the work lives, who can see it, and what happens next.",
  },
  {
    slug: "automation-and-integration",
    name: "Automation and integration",
    blurb:
      "Taking the repeated work off people, inside a system built around how you work.",
  },
  {
    slug: "web-and-commerce",
    name: "Web and commerce",
    blurb:
      "The public side: the site people judge you on, and the routes by which they buy, book and join.",
  },
];

export const SERVICES: readonly Service[] = [
  /* ── Software development ─────────────────────────────────────────────── */
  {
    slug: "custom-software-development",
    /* Lives where it already lives. See the note at the top of this file. */
    href: "/custom-software-for-businesses",
    name: "Custom software development",
    category: "software-development",
    hubBlurb:
      "Software built around how your business already works, instead of bending it to fit a generic tool.",
  },
  {
    slug: "web-app-development",
    href: "/services/web-app-development",
    name: "Web application development",
    category: "software-development",
    hubBlurb:
      "Software that runs in a browser, for the work your team does at a desk and your customers do at home.",
    page: {
      heading: "Web application development",
      metaTitle: "Web application development | Ownly Digital",
      metaDescription:
        "We build web applications for UK businesses: the software your team works in every day, running in a browser, on your own data, with no licence per seat. Based in Preston, Lancashire.",
      intro:
        "A web app is software your team opens in a browser and works in all day. No installing anything, no version that is out of date on one laptop, and no per seat licence for a product that does four fifths of what you need.",
      whatItIs: [
        "A web application is the middle ground between a spreadsheet and a product you buy. It has logins, its own database, screens built for the jobs your people actually do, and rules that stop the wrong thing being saved.",
        "It is the right shape when the work is done at a desk, when more than one person needs to see the same thing at the same time, and when what you need does not fit the tools you already pay for. Where one of those tools does part of the job well, we connect to it rather than rebuild it.",
      ],
      signs: [
        "The real system is a spreadsheet, and only one person fully understands it",
        "Two people have edited the same row and neither knows whose version is live",
        "You pay per seat for a tool where most of the seats use a quarter of it",
        "The process only works because somebody remembers to do a step that is written nowhere",
        "Getting a straight answer about the state of the work means asking three people",
      ],
      canBuild: [
        "Screens built around a job rather than a database table, so the work reads in the order it happens",
        "Accounts, roles and permissions, so people see what is theirs and nothing else",
        "An audit trail of who changed what and when",
        "Search and filtering across everything, rather than opening records one at a time",
        "Bulk actions, so a change to forty things is not forty clicks",
        "Exports that a finance person can open without being handed a weapon",
        "Notifications that reach the person who has to act, at the point they have to act",
      ],
      approach: [
        "We start from the work, not the screens. What happens now, who touches it, and where it currently goes wrong. Most of what a first version needs is already visible in the spreadsheet somebody has been maintaining by hand.",
        "The first release is deliberately narrow: the one part of the process that costs the most time, built properly and used in anger. Everything after that is decided by what the people using it hit first, which is rarely what anyone predicted.",
      ],
      builtIds: [
        "live-team-board",
        "a-days-work-decided-for-you",
        "customers-grouped-how-you-work",
        "ie-audit-trail",
        "ie-safe-csv",
        "numbers-that-cannot-disagree",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "internal-business-systems",
        "custom-software-development",
        "integrations-and-apis",
      ],
    },
  },
  {
    slug: "mobile-app-development",
    href: "/services/mobile-app-development",
    name: "Mobile app development",
    category: "software-development",
    hubBlurb:
      "iOS and Android apps, built when the thing has to be in somebody's pocket rather than on their desk.",
    page: {
      heading: "Mobile app development",
      metaTitle: "Mobile app development | Ownly Digital",
      metaDescription:
        "iOS and Android app development for UK businesses, by a team with apps live on both stores. We build the app, ship it to the stores, and look after it afterwards. Preston, Lancashire.",
      intro:
        "An app earns its place when the thing has to happen away from a desk, or when you need to reach somebody on the phone in their hand rather than in an inbox they check on Sundays.",
      whatItIs: [
        "A mobile app is not a smaller version of your website. It is worth building when it does something a browser cannot do well: work with a bad signal, use the camera properly, send a notification that actually arrives, or live on a home screen so it is opened without being remembered.",
        "If none of those apply, a good mobile website is cheaper, faster to change, and does not need anybody's permission to publish. We would rather tell you that than sell you an app.",
      ],
      signs: [
        "Your customers are on their feet, in a gym, on a site, at an event, not at a desk",
        "The important messages are going to email and are being read a day late",
        "People have to photograph or film something as part of the job",
        "You are paying for a platform that puts its own brand in front of your clients",
        "The thing people do with you is weekly or daily, not once a year",
      ],
      canBuild: [
        "One codebase that ships to both the App Store and Google Play",
        "Push notifications that land on the phone rather than in an inbox",
        "Media capture that survives a bad signal, uploading when the phone gets a connection back",
        "Offline tolerance, so a dead spot does not lose what somebody just typed",
        "Accounts, subscriptions and in app purchasing where the stores require it",
        "The store listing, screenshots, review submission and the resubmissions that follow",
        "An app that feels native down to the detail, rather than a website in a frame",
      ],
      approach: [
        "The stores are part of the work, not an afterthought. Review, rejection, resubmission and the rules about what may be sold inside an app all change what gets built, so they are decided at the start rather than discovered at the end.",
        "We have shipped to both stores, and the second app was faster than the first for reasons that had nothing to do with code. Knowing what a reviewer will object to is most of it.",
      ],
      builtIds: [
        "video-that-uploads-from-a-phone",
        "notification-that-lands-on-the-phone",
        "pf-haptics",
        "progress-photo-compare",
        "pf-fresh-on-return",
        "gs-get-app-redirect",
      ],
      caseStudySlugs: ["proflow-academy", "proflow-physiques", "growthsync"],
      relatedSlugs: [
        "memberships-and-subscriptions",
        "saas-and-software-products",
        "client-portals",
      ],
    },
  },
  {
    slug: "saas-and-software-products",
    href: "/services/saas-and-software-products",
    name: "SaaS and software products",
    category: "software-development",
    hubBlurb:
      "When the software is the thing you sell, not the thing that saves you time. A first version people can pay for.",
    page: {
      heading: "SaaS and software product development",
      metaTitle: "SaaS development and software products | Ownly Digital",
      metaDescription:
        "MVP and SaaS development for founders and businesses turning an idea, or an internal tool, into a product strangers pay for. We run our own product, so we build yours knowing what that costs.",
      intro:
        "Building software that earns money from strangers is a different job from building software that saves time for people you employ. It changes what gets built first, and it changes what has to be right before launch.",
      whatItIs: [
        "A product has to be signed up for by somebody who owes you nothing, understood without a training session, paid for on a card, and supported afterwards. Internal software has to do none of those things, which is why an internal tool rarely becomes a product by accident.",
        "The first version is not a smaller version of the finished idea. It is the one loop that has to work for anybody to pay: sign up, do the valuable thing, come back. Everything else can wait for people who are actually using it.",
      ],
      signs: [
        "You have built something internally and customers keep asking whether they can use it",
        "You are paying a platform a percentage to do something you could own outright",
        "The idea has been explained to enough people that you are confident somebody would pay",
        "You need something real in front of users before committing to the full build",
        "You want to know what it costs to run a product, not just to launch one",
      ],
      canBuild: [
        "Sign up, accounts, plans and the billing that goes with them",
        "A first release scoped to one loop, so it can be tested by real users rather than described",
        "Multi sided products where two groups have to find each other and both have to stay",
        "Product analytics, so you can see which features are used rather than guessing",
        "Onboarding that gets somebody to the valuable part before they lose interest",
        "The admin side: support, moderation, refunds and the ability to shut something down",
        "The legal plumbing a product needs: consent, data export, deletion and unsubscribes that work",
      ],
      approach: [
        "We run GrowthSync, our own product, which means we have paid for our own mistakes on app store review, on the cold start problem, and on features that looked essential and were used by nobody.",
        "We will push back on the feature list before the build starts. The most expensive thing in a first version is the part that was built because it was obvious rather than because a user needed it.",
      ],
      builtIds: [
        "gs-product-analytics",
        "gs-honest-match-score",
        "gs-safety-controls",
        "gs-take-your-data",
        "gs-invite-your-people",
        "gs-host-free-event",
      ],
      caseStudySlugs: ["growthsync"],
      relatedSlugs: [
        "mobile-app-development",
        "web-app-development",
        "memberships-and-subscriptions",
      ],
    },
  },

  /* ── Business systems ─────────────────────────────────────────────────── */
  {
    slug: "internal-business-systems",
    href: "/services/internal-business-systems",
    name: "Internal business systems",
    category: "business-systems",
    hubBlurb:
      "One place the work lives, with the dashboards and reporting that tell you what is happening in it.",
    page: {
      heading: "Internal business systems",
      metaTitle: "Internal business systems and operational software | Ownly Digital",
      metaDescription:
        "Custom internal systems for UK businesses: job and workflow management, dashboards, reporting and permissions, so the work lives in one place instead of a spreadsheet and three inboxes.",
      intro:
        "The system a business actually runs on is usually a spreadsheet, a shared inbox and somebody's memory. It works until the business grows, and then it fails quietly, in ways nobody finds out about for a fortnight.",
      whatItIs: [
        "An internal system is the software your own people work in: where a job lives from enquiry to invoice, who is responsible for it now, what state it is in, and what happens automatically when it moves.",
        "Dashboards and reporting are part of this rather than a separate purchase. A number is only useful if it comes from the same place the work is recorded, which is why reports bolted onto a system nobody uses properly always disagree with each other.",
      ],
      signs: [
        "Nobody can answer how many jobs are open right now without opening three tabs",
        "The weekly report is assembled by hand, and the person who assembles it is expensive",
        "Two sources of the same number disagree and there is a standing argument about which is right",
        "A job gets missed occasionally, always at the handover between two people",
        "New starters take weeks to be useful because the process only exists in people's heads",
      ],
      canBuild: [
        "A live board of what the team is working on and what state each job is in",
        "A screen that tells somebody what today is, rather than making them work it out",
        "Dashboards and weekly digests that arrive without anyone running a report",
        "Permissions, so a contractor sees their jobs and not your margins",
        "An audit trail, so a change can be traced to a person and a time",
        "Automations that tell you when they stop working, instead of failing silently",
        "Onboarding and training that lives in the system rather than leaving with the person",
        "Exports and integrations, so finance and the accountant get what they need without a request",
      ],
      approach: [
        "We map what happens now before proposing anything, including the parts that only work because somebody is quietly compensating. Those are usually the most valuable things to fix and the least likely to be written down.",
        "Then the smallest useful system goes live and gets used for real work. A system designed in full and delivered in full is a system designed around what people said they did, which is never quite what they do.",
      ],
      builtIds: [
        "live-team-board",
        "a-days-work-decided-for-you",
        "jobs-that-say-when-they-die",
        "something-looks-wrong-digest",
        "numbers-that-cannot-disagree",
        "ie-audit-trail",
        "new-starter-checklist",
        "training-that-stays-put",
        "job-finishes-itself",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "crm-development",
        "business-process-automation",
        "web-app-development",
      ],
    },
  },
  {
    slug: "crm-development",
    href: "/services/crm-development",
    name: "Custom CRM development",
    category: "business-systems",
    hubBlurb:
      "A CRM shaped like your pipeline, not a product you bend your business around and still pay per seat for.",
    page: {
      heading: "Custom CRM development",
      metaTitle: "Custom CRM development | Ownly Digital",
      metaDescription:
        "Custom CRM development for UK businesses: pipelines, contact records, quoting, automatic follow up, reporting and permissions, built around how you actually sell rather than how a product assumes you do.",
      intro:
        "Most businesses do not abandon their CRM because it lacked a feature. They abandon it because keeping it up to date was somebody's least favourite job, so the real pipeline went back to being a spreadsheet and a memory.",
      whatItIs: [
        "A CRM is where you keep who you are talking to, what you have said, what you have quoted, and what happens next. A custom one matters when your sales process has a shape a product does not have: a survey before a quote, a deposit before a slot, a specifier who is not the buyer.",
        "The test of one is not how much it records. It is whether the next action is obvious without anybody deciding, and whether the record gets updated by the work happening rather than by somebody remembering to type.",
      ],
      signs: [
        "The pipeline everyone trusts is a spreadsheet, and the CRM is where leads go to die",
        "Quotes go out and nobody is sure which ones were chased",
        "The same customer exists three times under three spellings",
        "A lead that went quiet six months ago is never looked at again",
        "You are paying per seat for a product where the important part still happens in email",
      ],
      canBuild: [
        "A pipeline with your stages, and a rule for what moves something between them",
        "Contact and company records that merge duplicates instead of collecting them",
        "Quoting inside the CRM, with the price arrived at the same way twice",
        "Follow up that happens whether or not anybody remembers, including when it is a bank holiday",
        "One next action per customer, so the list is a queue rather than a filing cabinet",
        "Dormant customers resurfaced on a schedule rather than on a whim",
        "Reporting on what is actually going to close, not on everything that was ever opened",
        "Roles and permissions, so the team sees its own work and the owner sees all of it",
      ],
      approach: [
        "Start with the pipeline as it really runs, including the stage everybody says is not a stage. Then decide what the system does on its own at each move, because a CRM that only records is a CRM somebody has to be paid to feed.",
        "Data comes over from whatever you are on now, duplicates and all, and the merge is part of the build rather than a job for somebody's evenings.",
      ],
      builtIds: [
        "click-to-call-crm",
        "automatic-chasing",
        "which-deals-will-close",
        "one-next-action",
        "same-customer-twice",
        "dormant-customers-resurfaced",
        "replies-matched-automatically",
        "knows-its-a-bank-holiday",
        "customers-grouped-how-you-work",
        "who-has-not-replied-yet",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "quoting-and-estimating",
        "business-process-automation",
        "internal-business-systems",
      ],
    },
  },
  {
    slug: "client-portals",
    href: "/services/client-portals",
    name: "Client and customer portals",
    category: "business-systems",
    hubBlurb:
      "A login where your client sees their own job, their own documents and their own next step.",
    page: {
      heading: "Client portal development",
      metaTitle: "Client portal development | Ownly Digital",
      metaDescription:
        "Client and customer portal development: a private login where clients see their job, their documents and what happens next, so the update email and the where are we call stop being somebody's day.",
      intro:
        "A portal replaces the update email. Instead of a client ringing to ask where something is, they open a page that already says, and your team gets the afternoon back that was being spent answering.",
      whatItIs: [
        "A client portal is a private area where somebody you work with can see their own thing: the state of the job, the documents, the approvals waiting on them, and what happens next.",
        "The useful ones are narrow. A portal that shows everything is a portal nobody reads, and the valuable part is almost always the one question clients ask most often, answered before they ask it.",
      ],
      signs: [
        "The same status question arrives from different clients every week",
        "Documents go back and forth as email attachments and the latest version is a guess",
        "You are asking clients to create an account somewhere just to sign one thing",
        "Onboarding a client means a chain of emails that stalls if anyone is on holiday",
        "Sensitive documents are being sent by email because it is the only way that is easy",
      ],
      canBuild: [
        "A login where a client sees their own job and nothing belonging to anybody else",
        "Documents behind a gate rather than on a public link anyone could forward",
        "A link that lets somebody sign or upload without creating an account at all",
        "Intake forms that file themselves into the system instead of arriving as a PDF",
        "E signature on a contract, with the signed copy stored against the job",
        "Approvals with a record of who agreed to what and when",
        "Notifications when something is waiting on the client, and a nudge when it stays waiting",
      ],
      approach: [
        "Decide first what the client is allowed to see, because that decision shapes everything else. A portal is a permissions problem before it is a design problem.",
        "Then take the single question your team answers most often and make the portal answer it. If that works, the rest is worth building. If nobody logs in, no amount of extra pages would have helped.",
      ],
      builtIds: [
        "client-portal",
        "token-link-documents",
        "documents-behind-a-gate",
        "client-intake-forms",
        "esign-retainer",
        "ie-feedback-link",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "internal-business-systems",
        "memberships-and-subscriptions",
        "web-app-development",
      ],
    },
  },
  {
    slug: "booking-systems",
    href: "/services/booking-systems",
    name: "Booking and scheduling systems",
    category: "business-systems",
    hubBlurb:
      "Taking a booking and a deposit without a phone call, and without sending people to somebody else's branded page.",
    page: {
      heading: "Booking system development",
      metaTitle: "Booking system development | Ownly Digital",
      metaDescription:
        "Custom booking and scheduling systems: deposits, reminders, availability rules and calendar sync, on your own site or inside your own app rather than on a third party page with somebody else's brand on it.",
      intro:
        "Every booking that needs a phone call is capped by how many phone calls you can take. Moving that online is usually the shortest distance between where a business is and where it wants to be.",
      whatItIs: [
        "A booking system takes an enquiry that would have been a call and turns it into a confirmed slot, usually with money attached. The hard part is rarely the calendar. It is the rules: who can be booked, when, for how long, at what price, and what happens when somebody does not turn up.",
        "Generic booking tools cover the simple cases, and if you already use one we can connect to it. They stop fitting when your availability depends on which person is being booked, when the price changes with the option chosen, or when the booking has to land in a system you already run.",
      ],
      signs: [
        "Bookings are agreed in messages and written into a calendar by hand",
        "No shows cost you because nothing is taken up front",
        "Your customers are sent to a third party page with somebody else's brand on it",
        "Two people have been booked into the same slot at least once",
        "Somebody spends part of every day sending reminders",
      ],
      canBuild: [
        "Availability that depends on the person, the service and the day, not one flat calendar",
        "Deposits, balances and instalments taken at the point of booking",
        "Prices that change with what is chosen, and that never differ from the price at checkout",
        "Booking inside your own app or your own site, branded as yours",
        "Sync into the calendar the team actually looks at",
        "Reminders and confirmations that go out on their own",
        "Rules that stop the wrong booking, such as a person who is not taking clients",
        "Prepaid blocks and session balances, counted down automatically",
      ],
      approach: [
        "Start with the rules, written out plainly. Most booking projects that go wrong went wrong because an edge case was discovered after launch: the double slot, the deposit that should not have been refundable, the coach who left.",
        "Money and calendar are wired together from the beginning. A booking that exists in the diary and not in the accounts is a booking somebody will have to reconcile by hand.",
      ],
      builtIds: [
        "in-app-branded-booking",
        "multi-step-booking-sync",
        "prepaid-session-tokens",
        "academy-pick-your-coach-pricing",
        "academy-unavailable-coach-cannot-be-booked",
        "gs-calendar-sync",
        "enquiry-into-the-system-you-already-use",
        "book-you-inside-your-own-app",
        "physiques-never-invents-a-checkout",
      ],
      caseStudySlugs: ["proflow-academy", "the-dfc-experience"],
      relatedSlugs: [
        "quoting-and-estimating",
        "memberships-and-subscriptions",
        "client-portals",
      ],
    },
  },
  {
    slug: "quoting-and-estimating",
    href: "/services/quoting-and-estimating",
    name: "Quoting and estimating systems",
    category: "business-systems",
    hubBlurb:
      "Pricing a job the same way twice, and turning a yes into a paid deposit before it cools off.",
    page: {
      /**
       * Titled at what people type, which is not what we called this internally.
       * Search Console for 20 Jul to 16 Sep 2026: the demand on this topic is
       * "instant quote" (56 impressions), "instant software estimate" (41),
       * "instant quote tool" (24), "quote tool" (13), then a long tail of
       * "instant estimator", "quote estimator", "online instant quote". The phrase
       * "quoting and estimating system development" appears in none of it. The old
       * title used our words, so the page was invisible to its own buyers.
       */
      heading: "Instant quote tools and estimating systems",
      metaTitle: "Instant quote tools and estimating software | Ownly Digital",
      metaDescription:
        "Instant quote tools built for your business: a price calculator on your own site, pricing rules that agree every time, and a deposit link at the moment of the yes.",
      intro:
        "Quoting is where most businesses lose money twice. Once on the hours spent producing quotes by hand, and again on the ones that were never followed up.",
      whatItIs: [
        "A quoting system holds your pricing rules in one place, so two people quoting the same job arrive at the same number, and a price can be produced while the customer is still interested.",
        "It runs in two directions. Facing the customer, it can give a real figure on your website so people self qualify before they ever speak to you. Facing your team, it turns an agreed number into a document, a deposit link and a follow up sequence without anybody retyping anything.",
      ],
      signs: [
        "Quotes take a morning and go out two days after the enquiry",
        "Two people price the same job differently and both are defensible",
        "You find out a quote was never chased when the customer buys elsewhere",
        "Enquiries arrive with no budget attached and half of them were never going to afford it",
        "The verbal yes and the paid deposit are days apart, and some of them never close that gap",
      ],
      canBuild: [
        "An instant price calculator on your own site, driven by a pricing model you control",
        "Pricing rules that produce the same number twice, whoever is quoting",
        "A quote document generated from the agreed figure rather than typed again",
        "A deposit or instalment payment link at the moment of the yes",
        "Follow up that chases the quote on a schedule and stops when the customer replies",
        "Reporting on what was quoted, what closed, and what quietly went cold",
        "A qualification step, so the quotes you spend time on are the ones worth having",
      ],
      approach: [
        "The pricing model is the project. Getting the rules out of somebody's head and into something explicit is most of the work, and it is usually the first time a business has seen its own pricing written down.",
        "The pricing model is written down before the interface is built. That is how the same job gets the same answer twice, whoever is quoting it.",
      ],
      builtIds: [
        "instant-quote-generator",
        "price-a-job-consistently",
        "scope-to-payment",
        "smart-intake-quiz",
        "project-qualification-quiz",
        "abandoned-enquiry-digest",
        "answer-first-lead-tool",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "crm-development",
        "business-process-automation",
        "booking-systems",
      ],
      /*
       * THIS PAGE INHERITED A RANKING, SO IT ANSWERS THE QUESTIONS THAT CAME WITH IT.
       *
       * `/work/instant-quote-estimator` used to carry these searches and now redirects here,
       * which is the right destination: the queries are "instant quote tool" and "quote tool",
       * and this is the page that sells one. But a redirect only moves the ranking. It does not
       * make the destination answer what the person typed.
       *
       * Search Console, 90 days to 2026-09-21, across the old page and this one: "instant
       * quote" at position 10, "instant software estimate" (43 impressions), "instant quote
       * tool", "instant online quote", "quote tool". Nought clicks between them.
       *
       * Two different people arrive on those searches and only one of them was being served.
       * Somebody who wants a quote tool BUILT is why this page exists. Somebody who wants a
       * price FOR THEMSELVES reached a page selling software with nothing telling them how to
       * get one, and the first answer closes that.
       *
       * IT DELIBERATELY DOES NOT POINT AT AN ESTIMATOR. This site used to run one at `/start`
       * and the case study about it was `/work/instant-quote-estimator`. Both now redirect,
       * `/start` to `/before-we-talk` and the study to this page, so the funnel is the briefing
       * and a call. Checked live on 2026-09-23 rather than assumed, because an answer promising
       * a costed range in two minutes would have been selling something that no longer exists.
       * If the estimator ever comes back, this answer is the first thing to update.
       */
      faqs: [
        {
          question: "Can I get a price for my own project without a call?",
          answer:
            "Partly. We price on what the software has to do rather than on how many pages it has, so the firm number comes out of one short conversation. Before that there is a briefing you can watch in your own time, which walks through real finished work and what it cost to get there, so you can judge the order of magnitude before speaking to anybody.",
        },
        {
          question: "What is an instant quote tool?",
          answer:
            "A price calculator on your own website, driven by your pricing rules rather than by a person. A customer picks the options that apply to their job and gets a real figure while they are still interested, instead of a form that promises somebody will be in touch.",
        },
        {
          question: "How accurate can an instant quote be?",
          answer:
            "As accurate as the pricing model behind it. Most quoting tools give a range rather than a single figure, which is usually the honest answer at that stage: enough for a customer to decide whether to keep talking, with the firm number agreed once the detail is known.",
        },
        {
          question: "What does my business need before it can quote instantly?",
          answer:
            "A pricing model somebody can write down: what drives the number, which options change it, and where the limits sit. If quotes currently come out of one person's head, getting that onto paper is the first piece of work. Most businesses that quote by hand are already consistent and have simply never had to describe how.",
        },
        {
          question: "Will an instant quote put customers off, or cost me negotiating room?",
          answer:
            "It filters, which is usually the point. People who were never going to afford it stop taking up your mornings, and the ones who continue have already accepted the order of magnitude. A range rather than a fixed figure keeps the detail of the final number where it belongs, in the conversation.",
        },
        {
          question: "Does this replace how we quote now, or sit alongside it?",
          answer:
            "Either. Some businesses want the calculator facing customers on the website. Others want it facing the team, so two people pricing the same job arrive at the same number and the agreed figure becomes a document, a deposit link and a follow up without anyone retyping it. Most end up with both.",
        },
      ],

    },
  },

  /* ── Automation and integration ───────────────────────────────────────── */
  {
    slug: "business-process-automation",
    /* Lives where it already lives. See the note at the top of this file. */
    href: "/business-automation-systems",
    name: "Business process automation",
    category: "automation-and-integration",
    hubBlurb:
      "The jobs that happen every week because somebody remembers to do them, happening on their own instead.",
  },
  {
    slug: "integrations-and-apis",
    href: "/services/integrations-and-apis",
    name: "Integrations and APIs",
    category: "automation-and-integration",
    hubBlurb:
      "Making your own system work with your accounts, payments and the other software it needs, and building the interface when there is not one.",
    page: {
      heading: "Software integrations and API development",
      metaTitle: "Software integrations and API development | Ownly Digital",
      metaDescription:
        "Integration work inside the systems we build for UK businesses: making your software work with the accounts, payments and booking tools you rely on, and building APIs and webhooks when the interface you need does not exist yet.",
      intro:
        "Integrations are one part of building the system your business runs on. Your own system has to work with your accounts package, card payments and anything else worth keeping, so nobody spends the day rowing between islands.",
      whatItIs: [
        "An integration moves information between two systems so nobody has to. The booking lands in the calendar, the paid invoice lands in the accounts, the enquiry lands in the CRM, and the record in each one agrees with the others.",
        "Where a system has no interface worth using, the work is to build one: an API for your own data, or a webhook that tells another system the moment something changes. That is also what makes your own software something other tools can be connected to later.",
      ],
      signs: [
        "The same information is typed into two systems by the same person",
        "A monthly export and import is somebody's recurring calendar entry",
        "Two systems disagree and nobody can say which is right",
        "A tool you pay for cannot see the thing that happened in the other tool you pay for",
        "An automation broke silently and was found out weeks later",
      ],
      canBuild: [
        "Connections between your own system and the software it needs to work with, in whichever direction is useful",
        "Data synchronisation that survives one side being down, rather than losing the change",
        "Webhooks, so something happens the moment it happens rather than on the hour",
        "APIs on your own systems, for your other tools or for a partner to build against",
        "Import and migration from a system you are leaving, duplicates resolved as part of it",
        "Monitoring, so a job that stops working says so rather than failing quietly",
        "A record of what moved and when, for the times when somebody asks",
      ],
      approach: [
        "The first question is always whether the integration should exist. Two systems that disagree are often better replaced by one that does not, and the integration would have been an expensive way to preserve a problem.",
        "Everything we build assumes the other side will fail at some point, because it will. Retries, a queue and an alert to a person are cheaper to build at the start than to add after the week somebody lost data.",
      ],
      builtIds: [
        "enquiry-into-the-system-you-already-use",
        "gs-calendar-sync",
        "jobs-that-say-when-they-die",
        "replies-matched-automatically",
        "multi-step-booking-sync",
        "ie-safe-csv",
        "gs-take-your-data",
      ],
      caseStudySlugs: [],
      relatedSlugs: [
        "business-process-automation",
        "internal-business-systems",
        "crm-development",
      ],
    },
  },

  /* ── Web and commerce ─────────────────────────────────────────────────── */
  {
    slug: "website-design-and-development",
    href: "/services/website-design-and-development",
    name: "Website design and development",
    category: "web-and-commerce",
    hubBlurb:
      "The site people judge you on, built to be edited by you and to earn its place in search.",
    page: {
      heading: "Website design and development",
      metaTitle: "Website design and development | Ownly Digital",
      metaDescription:
        "Website design and development for UK businesses: fast, accessible sites you can edit yourself, with a page for each thing people search for and one clear next step on every page.",
      intro:
        "A website is usually the cheapest thing we build and the first thing anyone judges you on. The difference between one that works and one that does not is rarely how it looks. It is whether it says what you do, in the words somebody would use to look for it.",
      whatItIs: [
        "A marketing site has one job: turn somebody who has heard of you, or found you, into an enquiry. Everything on it either helps that or is in the way.",
        "The part most sites get wrong is structure rather than design. One page describing eight services will not be found by anybody searching for one of them, and a site the owner cannot edit becomes out of date the first week something changes.",
      ],
      signs: [
        "The site was built years ago and nobody can change it without paying somebody",
        "You do a thing that is not on the website, or is one line on a page about six things",
        "Search sends you almost nothing, and what it sends is people typing your name",
        "The site looks fine on a laptop and fights you on a phone",
        "Every page ends in a different call to action, or in none at all",
      ],
      canBuild: [
        "A page for each service you sell, and for each area you actually work in",
        "Content you can edit yourself, without a developer and without breaking the layout",
        "A preview of a change before the public sees it",
        "Pages built to be found: titles, structure, metadata and schema that match what is visible",
        "Consent that is asked before anything measures anybody",
        "Enquiry forms that reach a person, resist bots, and file themselves where the work happens",
        "Performance and accessibility treated as requirements rather than as a later fix",
      ],
      approach: [
        "Structure first, which is the part that decides whether the site is ever found. Then the writing, then the design. A beautiful page about the wrong thing is still the wrong page.",
        "We hand over something you can run. If the only way to change a price is to email us, we have built you a liability.",
      ],
      builtIds: [
        "self-serve-cms",
        "a-page-for-every-place-you-work",
        "a-page-for-every-service-searched",
        "see-it-before-it-goes-live",
        "site-that-refreshes-itself",
        "mta-one-next-step-per-page",
        "mta-analytics-only-after-consent",
        "gs-search-landing-pages",
        "forms-that-bots-cannot-fill",
      ],
      caseStudySlugs: [
        "my-trusted-accountant",
        "proflow-academy#website",
        "the-dfc-experience",
      ],
      relatedSlugs: [
        "memberships-and-subscriptions",
        "booking-systems",
        "custom-software-development",
      ],
    },
  },
  {
    slug: "memberships-and-subscriptions",
    href: "/services/memberships-and-subscriptions",
    name: "Memberships and subscriptions",
    category: "web-and-commerce",
    hubBlurb:
      "Selling access rather than hours: recurring payment, a members area, and content released on your terms.",
    page: {
      heading: "Membership and subscription platforms",
      metaTitle: "Membership and subscription platform development | Ownly Digital",
      metaDescription:
        "Membership and subscription platforms: recurring payments, a members area, content released on a schedule and the admin behind it, owned by you rather than rented from a platform taking a cut.",
      intro:
        "Selling access instead of hours changes the business. It also changes the software, because the day somebody joins is the easy part and everything expensive happens in the months afterwards.",
      whatItIs: [
        "A membership platform handles who has paid, what that entitles them to, what happens when the payment fails, and how the thing they are paying for reaches them.",
        "Platforms that do this exist and are worth using when they fit. They stop fitting when the cut becomes real money, when your brand is behind theirs, or when what you are selling is not shaped like the courses and communities they were designed for.",
      ],
      signs: [
        "A platform takes a percentage of revenue you now know is worth owning",
        "Your members see somebody else's brand more than they see yours",
        "Content is released manually because there is no way to schedule it properly",
        "Failed payments are found out about late, or not at all",
        "What you sell does not fit the shape the platform expects, so you are working around it",
      ],
      canBuild: [
        "Plans, recurring payments, upgrades and the failure handling underneath them",
        "A members area, on the web or in your own app on both stores",
        "Content released on a schedule, or unlocked by progress rather than by date",
        "Groups, so one message reaches one slice of your members rather than everybody",
        "Check ins, progress tracking and the things that make somebody come back next week",
        "Cancellation and data export that behave properly, because the law expects it",
        "Admin for whoever runs it day to day, including refunds and shutting something down",
      ],
      approach: [
        "Retention is the design problem, not signup. We build the thing that gives a member a reason to open it in week six first, because that is the number the business lives on.",
        "Payment failure, cancellation and refunds get built at the start. They are the least interesting screens and the ones that decide whether the revenue is real.",
      ],
      builtIds: [
        "drip-video-academy",
        "custom-checkin-builder",
        "message-a-slice-of-your-customers",
        "everything-you-know-in-one-library",
        "build-once-reuse-forever",
        "academy-checkout-without-a-basket",
        "unsubscribes-that-actually-work",
        "gs-take-your-data",
      ],
      caseStudySlugs: ["proflow-academy", "elevate-physique-coaching"],
      relatedSlugs: [
        "mobile-app-development",
        "booking-systems",
        "saas-and-software-products",
      ],
    },
  },
];

/**
 * EVERY REFERENCE IS CHECKED AT IMPORT, AND A BAD ONE STOPS THE BUILD.
 *
 * These pages make claims of two kinds and only one of them is safe to get wrong. A capability
 * sentence is ours to write. A proof link is a promise that a real thing exists behind it, and a
 * renamed built entry or a retired case study would otherwise turn that promise into an empty
 * section or a dead link, on the page whose whole job is to be checkable.
 *
 * Throwing here rather than filtering quietly is deliberate: a proof section that silently
 * shrinks is exactly the failure nobody notices.
 */
const BUILT_IDS = new Set(
  BUILT_GROUPS.flatMap((group) => group.items.map((item) => item.id)),
);
const SERVICE_SLUGS = new Set(SERVICES.map((service) => service.slug));

for (const service of SERVICES) {
  const where = `services.ts: ${service.slug}`;
  if (!SERVICE_CATEGORIES.some((c) => c.slug === service.category)) {
    throw new Error(`${where} is in unknown category "${service.category}"`);
  }
  if (!service.page) continue;
  for (const id of service.page.builtIds) {
    if (!BUILT_IDS.has(id)) {
      throw new Error(`${where} cites built entry "${id}", which does not exist`);
    }
  }
  for (const slug of service.page.caseStudySlugs) {
    if (!resolveCaseStudyRef(slug)) {
      throw new Error(`${where} cites case study "${slug}", which does not exist`);
    }
  }
  for (const slug of service.page.relatedSlugs) {
    if (!SERVICE_SLUGS.has(slug)) {
      throw new Error(`${where} links to service "${slug}", which does not exist`);
    }
    if (slug === service.slug) {
      throw new Error(`${where} lists itself as a related service`);
    }
  }
}

/** The services that get a generated page under `/services/`. */
export const SERVICES_WITH_PAGES = SERVICES.filter(
  (service): service is Service & { page: ServicePage } => Boolean(service.page),
);

export function getService(slug: string): Service | undefined {
  return SERVICES.find((service) => service.slug === slug);
}

/** Services in a category, in declaration order, which is the order they are shown. */
export function servicesInCategory(category: ServiceCategorySlug): readonly Service[] {
  return SERVICES.filter((service) => service.category === category);
}

/** The built entries a service cites, resolved to the real items. */
export function builtItemsForService(page: ServicePage) {
  const items = BUILT_GROUPS.flatMap((group) =>
    group.items.map((item) => ({ ...item, group })),
  );
  return page.builtIds
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
}

/** The case studies a service cites, resolved. Empty means we have not delivered one yet. */
export function caseStudiesForService(page: ServicePage) {
  return page.caseStudySlugs
    .map((slug) => resolveCaseStudyRef(slug))
    .filter((study): study is NonNullable<typeof study> => Boolean(study));
}

/**
 * The reverse: the services that cite this case study.
 *
 * WHY THIS EXISTS. `caseStudySlugs` was only ever read in one direction, so a service page
 * pointed at its proof and the proof pointed nowhere back. `sectors.ts` already argues why that
 * rots: the pairing is written down once and the second half is the half nobody remembers to
 * add. Deriving it from the same registry means naming a case study on a service wires the link
 * both ways, and the validation above already guarantees the slug is real.
 *
 * IT IS ALSO THE SEO POINT. On 2026-09-13 `/work/instant-quote-estimator` was carrying
 * twenty-nine separate "instant quote" searches on its own, about 170 a month at positions 9 to
 * 15, while `/services/quoting-and-estimating` — the page that actually sells the thing those
 * people are looking for — was cited by it and linked from nowhere. The page with the rankings
 * was passing none of them to the page with the offer.
 *
 * Declaration order, so the first service listed is the primary one.
 */
export function servicesForCaseStudy(slug: string) {
  return SERVICES_WITH_PAGES.filter((service) =>
    service.page.caseStudySlugs.includes(slug),
  );
}

/**
 * The one service page a case study should send a reader to, or nothing.
 *
 * `primaryServiceSlug` on the study wins when it is set; otherwise the first service that cites
 * it, in declaration order. Throws if a study names a service that does not cite it back, so
 * the pairing cannot rot into a link that claims a relationship the registry does not have.
 */
export function primaryServiceForCaseStudy(study: {
  slug: string;
  primaryServiceSlug?: string;
}) {
  const candidates = servicesForCaseStudy(study.slug);
  if (!study.primaryServiceSlug) return candidates[0];
  const named = candidates.find(
    (service) => service.slug === study.primaryServiceSlug,
  );
  if (!named) {
    throw new Error(
      `Case study "${study.slug}" names primary service "${study.primaryServiceSlug}", which does not list it in caseStudySlugs`,
    );
  }
  return named;
}
